import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import { curriculumApi } from '../api/curriculum';

export function useTemplates() {
  return useQuery({
    queryKey: queryKeys.curriculum.templates(),
    queryFn: async () => {
      const { data } = await curriculumApi.listTemplates();
      return data ?? [];
    },
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: queryKeys.curriculum.template(id),
    queryFn: async () => {
      const { data } = await curriculumApi.getTemplate(id);
      return data;
    },
    enabled: !!id,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { title: string; slug: string; description?: string }) =>
      curriculumApi.createTemplate(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.curriculum.templates() }),
  });
}

export function useUpdateTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      curriculumApi.updateTemplate(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.curriculum.templates() });
      qc.invalidateQueries({ queryKey: queryKeys.curriculum.template(id) });
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => curriculumApi.deleteTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.curriculum.templates() }),
  });
}

export function useCreateModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: curriculumApi.createModule,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.curriculum.all }),
  });
}

export function useUpdateModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      curriculumApi.updateModule(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.curriculum.all }),
  });
}

export function useDeleteModule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => curriculumApi.deleteModule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.curriculum.all }),
  });
}

export function useCreateTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: curriculumApi.createTopic,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.curriculum.all }),
  });
}

export function useUpdateTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      curriculumApi.updateTopic(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.curriculum.all }),
  });
}

export function useDeleteTopic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => curriculumApi.deleteTopic(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.curriculum.all }),
  });
}

export function useTemplateBatches(id: string | null) {
  return useQuery({
    queryKey: ['curriculum', 'template-batches', id],
    queryFn: async () => {
      const { data } = await curriculumApi.listTemplateBatches(id!);
      return data ?? [];
    },
    enabled: !!id,
  });
}

export function useForkTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, batchId }: { templateId: string; batchId: string }) =>
      curriculumApi.forkTemplate(templateId, batchId),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.curriculum.templates() }),
  });
}
